import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, Alert, 
    CircularProgress, Modal, TextField, Divider, MenuItem, Select, FormControl, InputLabel
} from '@mui/material'; 
import { CheckCircle, Pending, Error, Visibility } from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const style = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 600, bgcolor: 'background.paper', boxShadow: 24, p: 4, maxHeight: '90vh', overflowY: 'auto', borderRadius: 2
};

const HRBankVerification = () => {
    const { user } = useAuth();
    // Logic to determine if user has global viewing permissions
    const isHRorOwner = user?.roles.includes('ROLE_HR') || user?.roles.includes('ROLE_OWNER');
    
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [hrRemark, setHrRemark] = useState('');
    const [editFormData, setEditFormData] = useState({});
    const [isVerifiedOption, setIsVerifiedOption] = useState(false);

    /**
     * FETCH LOGIC: Updated to request 'global' data for cross-branch HR visibility.
     * This ensures all employees from all branches appear in the queue.
     */
    const fetchReport = async () => {
        if (!user) return;
        setLoading(true);
        setError('');
        try {
            // FIX: Always use 'global' for HR/Owner to see all branch employees
            const branchIdParam = isHRorOwner ? 'global' : user.branchId; 
            
            if (!branchIdParam) {
                setError('User configuration error: Branch context missing.');
                setLoading(false);
                return;
            }

            // Path matches updated EmployeeController.java @GetMapping
            const response = await api.get(`/employees/branch/${branchIdParam}/report/bank-status`); 
            setReport(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch bank verification report.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, [user, isHRorOwner]);

    const handleOpenModal = async (reportItem) => {
        setLoading(true);
        try {
            // API Endpoint: GET /api/bank-details/employee/{employeeId}
            const response = await api.get(`/bank-details/employee/${reportItem.employeeId}`);
            setSelectedDetail(response.data);
            setEditFormData(response.data);
            setHrRemark(response.data.hrRemark || '');
            setIsVerifiedOption(response.data.verifiedByHr || false);
            setModalOpen(true);
        } catch (err) {
            toast.error('Failed to fetch detailed bank record.');
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedDetail(null);
        setHrRemark('');
        setEditFormData({});
    };

    const handleFieldChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    const handleVerificationUpdate = async () => {
        setLoading(true);
        const requestBody = {
            bankName: editFormData.bankName,
            accountNumber: editFormData.accountNumber,
            ifsc: editFormData.ifsc,
            branch: editFormData.branch,
            upi: editFormData.upi,
            verifiedByHr: isVerifiedOption,
            hrRemark: hrRemark || (isVerifiedOption ? 'Verified successfully by HR.' : 'Details unverified/rejected.')
        };

        try {
            // API Endpoint: PUT /api/bank-details/employee/{employeeId}/detail/{bankDetailsId}
            await api.put(`/bank-details/employee/${selectedDetail.employeeId}/detail/${selectedDetail.id}`, requestBody);
            toast.success("Bank details updated and verified successfully.");
            fetchReport();
            handleModalClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Update failed.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'VERIFIED': return <CheckCircle color="success" />;
            case 'PENDING_OR_ABSENT': return <Pending color="warning" />;
            default: return <Error color="error" />;
        }
    };

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Bank Details Verification Queue</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Review and verify bank account information for employees across all branches.
            </Typography>
            
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && !selectedDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Employee ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Bank Details ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {report.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        No employee bank records found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                report.map((row) => (
                                    <TableRow key={row.employeeId} hover>
                                        <TableCell>{row.employeeId}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{row.bankDetailsId}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getStatusIcon(row.isVerified)}
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.isVerified}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {row.bankDetailsId !== 'N/A' && (
                                                <Button 
                                                    variant="outlined" 
                                                    size="small" 
                                                    startIcon={<Visibility />}
                                                    onClick={() => handleOpenModal(row)}
                                                >
                                                    Review/Verify
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedDetail && (
                <Modal open={modalOpen} onClose={handleModalClose}>
                    <Box sx={style}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>Verify Bank Details (ID: {selectedDetail.id})</Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Alert severity={selectedDetail.verifiedByHr ? "success" : "warning"} sx={{ mb: 2 }}>
                            Current Status: {selectedDetail.verifiedByHr ? "VERIFIED" : "PENDING"}
                        </Alert>
                        
                        <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold', color: '#1e293b' }}>Employee Bank Data (Editable):</Typography>
                        <TextField fullWidth margin="normal" name="bankName" label="Bank Name" value={editFormData.bankName || ''} onChange={handleFieldChange} />
                        <TextField fullWidth margin="normal" name="accountNumber" label="Account Number" value={editFormData.accountNumber || ''} onChange={handleFieldChange} />
                        <TextField fullWidth margin="normal" name="ifsc" label="IFSC Code" value={editFormData.ifsc || ''} onChange={handleFieldChange} />
                        <TextField fullWidth margin="normal" name="branch" label="Branch" value={editFormData.branch || ''} onChange={handleFieldChange} />
                        <TextField fullWidth margin="normal" name="upi" label="UPI ID" value={editFormData.upi || ''} onChange={handleFieldChange} />
                        
                        <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 'bold', color: '#1e293b' }}>HR Action:</Typography>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Verification Status</InputLabel>
                            <Select
                                value={isVerifiedOption}
                                label="Verification Status"
                                onChange={(e) => setIsVerifiedOption(e.target.value)}
                            >
                                <MenuItem value={true}><CheckCircle color="success" sx={{ mr: 1 }} /> VERIFIED</MenuItem>
                                <MenuItem value={false}><Error color="error" sx={{ mr: 1 }} /> UNVERIFIED / REJECTED</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField 
                            fullWidth multiline rows={3} margin="normal" 
                            label="HR Remark" 
                            placeholder="Add reason for verification or rejection..."
                            value={hrRemark} 
                            onChange={(e) => setHrRemark(e.target.value)} 
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                            <Button variant="outlined" onClick={handleModalClose}>Cancel</Button>
                            <Button 
                                variant="contained" 
                                color={isVerifiedOption ? "success" : "error"} 
                                onClick={handleVerificationUpdate} 
                                startIcon={isVerifiedOption ? <CheckCircle /> : <Error />}
                                disabled={loading}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Verification Status'}
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            )}
        </Box>
    );
};

export default HRBankVerification;